<template>
  <div id="app">
    <div id="map" class="map">
      <Components3 ref="Components3Ref" @change="changeMap" @handleLocation="handleLocation" />
      <!-- 右下角控制信息容器 -->
      <div class="map-controls-container">
        <!-- 缩放级别显示 -->
        <div class="zoom-level-display">{{ $t('缩放级别') }}: {{ zoomLevel }}</div>

        <!-- 比例尺容器 -->
        <div id="scale-line-container" class="scale-line-custom-container"></div>
        <!-- 坐标显示（可点击切换） -->
        <div class="coordinate-display" @click="toggleCoordinateProjection">
          X: {{ formattedCoordinate.x }} Y: {{ formattedCoordinate.y }}
        </div>
      </div>
      <div id="popup" v-show="visible">
        <div class="popup-header">
          <span>{{ $t('详情') }}</span>
          <a-icon type="close" @click="closePopup" />
        </div>
        <a-list bordered class="popup-list">
          <a-list-item> {{ $t('项目编码') }}:{{ clickCurrentRow.projectCode || clickCurrentRow.project_code }} </a-list-item>
          <a-list-item v-if="clickCurrentRow.topology_code || clickCurrentRow.topologyCode"> {{ $t('拓扑编码') }}:{{ clickCurrentRow.topologyCode || clickCurrentRow.topology_code }} </a-list-item>
          <a-list-item> {{ $t('编码') }}:{{ clickCurrentRow.code }} </a-list-item>
          <a-list-item> {{ $t('名称') }}:{{ clickCurrentRow.name }} </a-list-item>
          <a-list-item v-if="clickCurrentRow.type == 'NODE_PIPELINE_TYPE_PIPELINE'"> {{ $t('类型') }}: {{ $t('管线') }}</a-list-item>
          <a-list-item v-else-if="clickCurrentRow.type"> {{ $t('类型') }}:{{ filterDictTextByCache('node_type',clickCurrentRow.type) }} </a-list-item>
          <a-list-item v-if="clickCurrentRow.height>=0"> {{ $t('高程') }}:{{ clickCurrentRow.height }} </a-list-item>
          <a-list-item v-if="clickCurrentRow.type == 'NODE_PIPELINE_TYPE_PIPELINE'"> {{ $t('材质') }}:{{ clickCurrentRow.materialType }} </a-list-item>
        </a-list>
      </div>
    </div>
  </div>
</template>

<script>
import Overlay from 'ol/Overlay.js'
import 'ol/ol.css'
import Map from 'ol/Map'
import View from 'ol/View'
import { get as getProjection, fromLonLat, toLonLat } from 'ol/proj'
import { createLayerEm_topology_node_pipeline } from '../module/layers/Em_topology_node_pipeline.js'
import { createLayerEM_MAP_ALL_18 } from '../module/layers/EM_MAP_ALL_18_DARK.js'
import { createLayerTp_node } from '../module/layers/Tp_node.js'
import { createLayerTp_pipeline } from '../module/layers/Tp_pipeline.js'
import { createLayerTp_area } from '../module/layers/Tp_area.js'
import { createGetFeatureInfo } from '../module/comFunction/WmsGetFeatureInfo.js'
import { parseHexWKB, zoomToPoint, zoomToLine } from '../module/comFunction/ComFunction.js'
import { updateOrCreateGeoServerStyle } from '../module/comFunction/SLDFunction.js'
import { Vector as VectorLayer } from 'ol/layer'
import { Vector as VectorSource } from 'ol/source'
import { vectorStyle } from '../module/style/VectorStyle.js'
import { Feature } from 'ol'
import CONFIG from '../module/config/GIS_Config'
import Vue from 'vue'
import ScaleLine from 'ol/control/ScaleLine'
import { getPostGreSqlNodeInfoByCode, getPostGreSqlLineInfoByCode,getPostGreSqlAreaInfoByCode } from '../module/comFunction/ApiFunction.js'
import Components3 from '../components/Components3.vue'
import Components from '../components/Components.vue'
import Components0 from '../components/Components0.vue'
import {filterDictTextByCache} from '@/components/dict/JDictSelectUtil'

export default {
  name: 'App',
  components: { Components3, Components0, Components },
  data() {
    return {
      filterDictTextByCache: filterDictTextByCache,
      map: {},
      tileLayer: {},
      baseLayer: {},
      nodeLayerNode: {},
      pipeLineLayer: {},
      areaLayer: {},
      highlightLayer: {}, // 高亮矢量图层
      tileSource: {},
      view: {},
      resolutions: [],
      matrixIds: [],
      projection: '',
      clickCurrentRow: {
        project_code: '',
        topology_code: '',
        code: '',
        name: '',
        type: '',
      },
      visible: false,
      zoomLevel: 0, //
      topology: '', // 全局拓扑参数
      scaleDisplayInterval: null, // 比例尺更新定时器
      coordinateProjection: 'EPSG:3857', // 当前坐标系（3857或4326）
      currentCoordinate: null,
    }
  },
  computed: {
    // 格式化坐标显示
    formattedCoordinate() {
      if (!this.currentCoordinate) {
        return { x: '--', y: '--' }
      }

      if (this.coordinateProjection === 'EPSG:3857') {
        // 3857坐标系显示，保留3位小数
        return {
          x: this.currentCoordinate.x.toFixed(3),
          y: this.currentCoordinate.y.toFixed(3),
        }
      } else {
        // 4326坐标系显示（经纬度），保留6位小数
        const lonLat = toLonLat([this.currentCoordinate.x, this.currentCoordinate.y])
        return {
          x: lonLat[0].toFixed(6),
          y: lonLat[1].toFixed(6),
        }
      }
    },
  },
  mounted() {
    // 确保DOM加载完了再初始化地图
    this.$nextTick(() => {
      this.initMap()
      // 新增点击事件监听
      this.map.on('click', this.handleMapClick)
      // 添加高亮图层
      this.initHighlightLayer()
      this.map.addLayer(this.highlightLayer)
      // 初始化详情页Overlay
      this.popup = new Overlay({
        element: document.getElementById('popup'),
        className: 'custom-popup', // 自定义样式类
      })
      this.map.addOverlay(this.popup)
      // 添加定时器，用于更新图层参数
      this.setupScaleDisplay()
      // 添加鼠标位置监听器
      this.setupMousePositionDisplay()
    })
  },
  beforeDestroy() {
    // 清除定时器
    if (this.scaleDisplayInterval) {
      clearInterval(this.scaleDisplayInterval)
    }
  },
  created() {
    // 添加测试数据
  },
  methods: {
    initMap() {
      // 1. 投影配置 编码
      this.projection = getProjection(CONFIG.PROJECTION)
      // 从 WMTS 图层元数据中获取范围（假设服务端提供）
      let wmtsLayerExtent = CONFIG.DEFAULT_EXTENT

      // 图层组
      this.tileLayer = createLayerEm_topology_node_pipeline()
      this.tileSource = this.tileLayer.getSource()
      // 底图
      this.baseLayer = createLayerEM_MAP_ALL_18()
      // 点 图层
      this.nodeLayerNode = createLayerTp_node()
      // 线图层
      this.pipeLineLayer = createLayerTp_pipeline()
      // 区域图层
      this.areaLayer = createLayerTp_area()

      // 获取指定图层的范围
      // try {
      //   wmtsLayerExtent = getWMTSExtent('EM_MAP_ALL_18')
      //   console.log('图层范围:', extent) // [minX, minY, maxX, maxY]
      // } catch (error) {
      //   console.error('初始化失败，使用默认范围', error)
      //   // 降级方案
      //   wmtsLayerExtent = [83.533098, 46.42201, 83.910157, 46.588895]
      // }

      // 2. 动态计算中心点
      const center = [
        (wmtsLayerExtent[0] + wmtsLayerExtent[2]) / 2, // 经度平均值
        (wmtsLayerExtent[1] + wmtsLayerExtent[3]) / 2, // 纬度平均值
      ]

      // 3. 转换为投影坐标系
      const projectedCenter = fromLonLat(center)
      const projectedExtent = [
        ...fromLonLat([wmtsLayerExtent[0], wmtsLayerExtent[1]]),
        ...fromLonLat([wmtsLayerExtent[2], wmtsLayerExtent[3]]),
      ].flat()

      // 4. 创建视图
      this.view = new View({
        center: projectedCenter,
        zoom: 11,
        projection: this.projection,
        // extent: projectedExtent, // 动态设置视图范围
      })

      // 7. 创建地图实例
      this.map = new Map({
        target: 'map',
        layers: [this.baseLayer, this.pipeLineLayer, this.nodeLayerNode, this.areaLayer],
        view: this.view,
        // 添加比例尺控件
        controls: [
          new ScaleLine({
            className: 'ol-scale-line', // 自定义类名，用于样式覆盖
            units: 'metric', // 公制单位（米、公里）
            bar: false, // 使用条形比例尺（横向线段）
            steps: 1, // 比例尺分段数，这里设置为4段（默认是4，也可以不设置）
            text: false, // 显示比例尺文本
            minWidth: 80, // 最小宽度（像素）
            target: document.getElementById('scale-line-container'),
          }),
        ],
      })

      // +++ 新增：使用fit方法设置初始范围 +++
      this.$nextTick(() => {
        this.map.getView().fit(projectedExtent, {
          padding: [20, 20, 20, 20], // 添加适当的内边距
          duration: 0, // 立即生效
        })
      })
    },
    // 初始化高亮图层
    initHighlightLayer() {
      this.highlightLayer = new VectorLayer({
        source: new VectorSource(),
        style: feature => vectorStyle(feature), // 使用函数样式
        zIndex: 9999, // 确保在最上层
      })
    },
    // 设置鼠标位置显示
    setupMousePositionDisplay() {
      this.mouseMoveListener = this.map.on('pointermove', event => {
        const coordinate = event.coordinate
        this.currentCoordinate = {
          x: coordinate[0],
          y: coordinate[1],
        }
      })
    },
    // 切换坐标系
    toggleCoordinateProjection() {
      this.coordinateProjection = this.coordinateProjection === 'EPSG:3857' ? 'EPSG:4326' : 'EPSG:3857'
    },
    // 点击事件 获取点 线 信息
    async handleMapClick(evt) {
      let that = this
      const coordinate = evt.coordinate
      console.log('点击左键evt属性:', coordinate)
      const view = this.map.getView()
      const resolution = view.getResolution()
      const projection = view.getProjection()

      // 2. 如果没获取到矢量要素，改用WMS GetFeatureInfo查询
      try {
        const layers = [
          { layer: this.nodeLayerNode, name: 'tp_node_sql' },
          { layer: this.pipeLineLayer, name: 'tp_pipeline_sql' },
          { layer: this.areaLayer, name: 'tp_area_sql' },
        ]
        // 查询点击信息
        const results = await Promise.all(
          layers.map(async layerInfo => {
            const info = await createGetFeatureInfo({
              map: this.map,
              coordinate,
              layer: layerInfo.layer,
              layerName: layerInfo.name,
              resolution,
              projection,
            })
            return { ...layerInfo, info }
          })
        )
        console.log('查询结果:', results)
        // 展示详情弹窗
        if (results[0].info.features.length > 0) {
          that.clickCurrentRow = Object.assign({}, results[0].info.features[0].properties, {
            id: results[0].info.features[0].id,
          })
          // 显示详情
          this.popup.setPosition(coordinate)
          that.visible = true
          // 获取当前code详情
          let item = await this.getNodeInfo(that.clickCurrentRow.code)
          // 高亮
          this.parseAndHighLight(item)
        } else if (results[1].info.features.length > 0) {
          that.clickCurrentRow = Object.assign({}, results[1].info.features[0].properties, {
            id: results[1].info.features[0].id,
          })
          // 显示详情
          this.popup.setPosition(coordinate)
          that.visible = true
          // 获取当前code详情
          let item = await this.getLineInfo(that.clickCurrentRow.code)
          // 高亮
          this.parseAndHighLight(item)
        } else if(results[2].info.features.length > 0){
          that.clickCurrentRow = Object.assign({}, results[2].info.features[0].properties)
          this.popup.setPosition(coordinate)
          that.visible = true
          let item = await this.getAreaInfo(that.clickCurrentRow.id)
          this.handleListClick(item)
        } else {
          // 关闭Overlay
          this.popup.setPosition(undefined)
          this.visible = false
          // 清除之前的高亮要素
          const highlightSource = this.highlightLayer.getSource()
          highlightSource.clear()
        }
        // 处理所有图层的结果
        results.forEach(result => {
          if (result.info && result.info.features.length > 0) {
            console.log(`${result.name} 图层属性:`, result.info.features)
            result.info.features.forEach(feature => {
              console.log('详细属性:', feature.properties)
            })
          }
        })
      } catch (error) {
        console.error('WMS查询失败:', error)
      }
    },
    async getNodeInfo(code) {
      if (code == null) return null
      let parm = {
        projectCode: CONFIG.TopologyProjection,
        topologyCode: CONFIG.TopologyCode,
        code: code,
        name: null,
      }
      try {
        const res = await getPostGreSqlNodeInfoByCode(parm)
        if (res.success) {
          console.log('当前线信息', res.result)
          return res.result
        } else {
          return null
        }
      } catch (error) {
        console.error('获取线信息失败:', error)
        return null
      }
    },
    async getLineInfo(code) {
      if (code == null) return null
      let parm = {
        projectCode: CONFIG.TopologyProjection,
        topologyCode: CONFIG.TopologyCode,
        code: code,
        name: null,
      }
      try {
        const res = await getPostGreSqlLineInfoByCode(parm)
        if (res.success) {
          console.log('当前线信息', res.result)
          return res.result
        } else {
          return null
        }
      } catch (error) {
        console.error('获取线信息失败:', error)
        return null
      }
    },
    async getAreaInfo(code) {
      if (code == null) return null
      try {
        const res = await getPostGreSqlAreaInfoByCode(code)
        if (res.success) {
          return res.result
        } else {
          return null
        }
      } catch (error) {
        return null
      }
    },
    parseAndHighLight(item) {
      if (item == null) return
      // 解析十六进制geometry坐标。
      const geometry = parseHexWKB(item.geometry, 'EPSG:3857', 'EPSG:3857')
      if (!geometry) return

      // 清除之前的高亮要素
      const highlightSource = this.highlightLayer.getSource()
      highlightSource.clear()

      // 创建新要素并添加到高亮图层
      const feature = new Feature({
        geometry: geometry,
        featureType: item.id.startsWith('line') ? 'line' : 'point', // 标识类型
      })
      highlightSource.addFeature(feature)
      return geometry
    },
    async handleListClick(item) {
      if (!this.map) return
      const view = this.map.getView()

      // 解析十六进制geometry坐标。
      const geometry = this.parseAndHighLight(item)

      // 根据几何类型定位
      const geomType = geometry.getType()

      if (geomType === 'Point') {
        const coordinate = geometry.getCoordinates()
        await zoomToPoint(coordinate, view)
        // 设置弹窗位置
        this.clickCurrentRow = item
        this.popup.setPosition(coordinate)
        this.visible = true
      } else if (geomType === 'MultiLineString') {
        // 修复点1: 计算线要素的实际中心点
        const extent = geometry.getExtent()
        const center = [(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2]

        // 修复点2: 等待缩放动画完成
        await zoomToLine(geometry, view)

        // 修复点3: 使用计算出的中心点设置弹窗
        this.clickCurrentRow = item
        this.popup.setPosition(center)
        this.visible = true
      } else if(geomType === 'MultiPolygon'){
        // 修复点1: 计算线要素的实际中心点
        const extent = geometry.getExtent()
        const center = [(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2]

        // 修复点2: 等待缩放动画完成
        await zoomToLine(geometry, view)

        // 修复点3: 使用计算出的中心点设置弹窗
        this.clickCurrentRow = item
        this.popup.setPosition(center)
        this.visible = true
      } else {
        this.closePopup()
      }
    },
    closePopup() {
      this.popup.setPosition(undefined)
      this.visible = false
      // 清除之前的高亮要素
      const highlightSource = this.highlightLayer.getSource()
      highlightSource.clear()
    },
    // +++ 新增：设置比例尺显示和参数更新 +++
    setupScaleDisplay() {
      // 初始化缩放层级
      this.updateZoomLevel()

      // 添加地图缩放监听
      this.map.getView().on('change:resolution', () => {
        this.updateZoomLevel()
      })
      // 定期更新图层参数
      this.scaleDisplayInterval = setInterval(() => {
        this.updateLayerParams()
      }, 500)
    },
    // +++ 新增：更新缩放层级 +++
    updateZoomLevel() {
      if (this.map && this.map.getView()) {
        const currentZoom = this.map.getView().getZoom()
        // 四舍五入到整数（如果需要小数可以去掉）
        this.zoomLevel = Math.round(currentZoom)
      }
    },
    // 更新所有图层的参数
    updateLayerParams() {
      // 更新拓扑参数（如果缓存有变化）
      // 更新所有图层的参数
      const layers = [this.nodeLayerNode, this.pipeLineLayer, this.areaLayer]

      layers.forEach(layer => {
        if (layer && layer.getSource()) {
          layer.getSource().updateParams({
            VIEWPARAMS:
              'projectCode:' +
              CONFIG.TopologyProjection +
              ';topologyCode:' +
              CONFIG.TopologyCode +
              ';zoomLevel:' +
              this.zoomLevel,
          })

          // 强制刷新图层（解决缓存问题）
          layer.getSource().refresh()
        }
      })
    },
    refreshPipelineLayer() {
      const source = this.pipeLineLayer.getSource()
      source.updateParams({
        STYLES: 'Dynamic_Pipeline_Style',
        t: Date.now(), // 防止缓存
      })
      this.$nextTick(() => {
        this.map.updateSize()
      })
    },
    testPoint() {
      updateOrCreateGeoServerStyle().then(() => {})
    },
    changeMap(){
      this.areaLayer.getSource().updateParams({
        _t: new Date().getTime()
      });
      this.areaLayer.getSource().refresh();
      this.closePopup();
    },
    handleLocation(item){
      this.handleListClick(item);
    }
  },
}
</script>

<style>
#map {
  width: 100%;
  height: 80vh;
  position: relative;
}
#popup {
  background: rgba(70, 76, 92, 0.8); /* Components2的半透明白色背景 */
  border-radius: 6px;
  color: white;
  width: 200px;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.12);

  .popup-header {
    display: flex;
    justify-content: space-between;
    padding: 8px 12px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.2);

    .anticon-close {
      cursor: pointer;
      &:hover {
        color: #1890ff;
      }
    }
  }

  .popup-list {
    max-height: 300px;
    overflow-y: auto;

    .ant-list-item {
      color: white;
      border-color: rgba(255, 255, 255, 0.2);
      padding: 8px 12px;
    }
  }
}
/* +++ 新增比例尺样式 +++ */
/* 自定义比例尺控件样式 */
.ol-scale-line {
  background: rgba(255, 255, 255, 0.5); /* 半透明白色背景 */
  border-radius: 4px;
  bottom: 8px;
  left: 8px; /* 默认在左下角，可以调整位置 */
  padding: 2px;
  position: absolute;
}

.ol-scale-line-inner {
  border: 1px solid #333;
  border-top: none;
  color: #333;
  font-size: 12px;
  text-align: center;
  margin: 1px;
  will-change: contents, width;
  transition: all 0.25s;
  /* 禁止用户选择文本 */
  user-select: none;
  pointer-events: none;
}

/* 如果想要将比例尺放在右下角，可以这样调整 */
.ol-scale-line {
  left: auto;
  right: 8px;
}
/* 右下角控制信息容器 */
.map-controls-container {
  position: absolute;
  bottom: 20px;
  right: 20px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  z-index: 2;
  gap: 5px;
}

/* 缩放级别显示 */
.zoom-level-display {
  background: rgba(255, 255, 255, 0.5);
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 12px;
  color: #333;
  font-weight: normal;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  user-select: none;
  margin-bottom: 13px;
}

/* 坐标显示 - 可点击切换 */
.coordinate-display {
  background: rgba(255, 255, 255, 0.5);
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 12px;
  color: #333;
  font-weight: bold;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  cursor: pointer;
  text-align: center;
  transition: all 0.3s;
  user-select: none;

  /* 等宽字体确保对齐 */
  font-family: 'Courier New', Courier, monospace;

  /* 在可点击元素上添加悬停效果 */
  &:hover {
    background: rgba(245, 245, 245, 0.95);
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3);
  }

  /* 添加坐标系提示 */
  .projection-hint {
    font-size: 10px;
    color: #666;
    margin-top: 2px;
    font-weight: normal;
    font-style: italic;
  }
}

/* 自定义比例尺容器 */
.scale-line-custom-container {
  background: rgba(255, 255, 255, 0.5);
  border-radius: 4px;
  padding: 4px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}
.scale-line-custom-container .ol-scale-line {
  margin-bottom: 24px;
}

/* 比例尺样式 */
.scale-line-custom .ol-scale-line {
  position: static;
  background: none;
}

/* 比例尺文本 */
.scale-line-custom .ol-scale-text {
  font-size: 11px;
  font-weight: bold;
  color: #333;
  text-shadow: 0 0 2px white;
  text-align: center;
  margin-top: 2px;
}

/* 比例尺条 */
.scale-line-custom .ol-scale-bar {
  background: #333;
  height: 3px;
  margin-top: 2px;
}
</style>
